// Dependencies
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// DIBK Design
import { Button, Container, Header, LoadingAnimation, Paper } from "dibk-design";

// Partials
import MainHeading from "components/partials/MainHeading";

// Actions
import { fetchSubmission } from "actions/SubmissionActions";
import { fetchSelectedForm } from "actions/FormActions";
import { getSignedDocument } from "actions/SigningActions";
import { updateLoadingMessage } from "actions/LoadingMessageActions";

// Helpers
import { formatContactInfoText } from "helpers/formatHelpers";
import { saveFileContentFromBlob } from "helpers/fileHelpers";

// Stylesheets
import commonStyle from "components/routes/common.module.scss";

// Automatisk oppdatering mens signeringen behandles server-side. Poll raskt de første gangene
// (den ferske signeringen blir vanligvis "signert" innen få sekunder), deretter roligere.
const MAX_STATUS_POLLS = 24;
const FAST_STATUS_POLLS = 10; // Antall raske forsøk før vi faller tilbake til det rolige intervallet.
const FAST_STATUS_POLL_INTERVAL_MS = 1500;
const STATUS_POLL_INTERVAL_MS = 5000;

const Receipt = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();

    // Redux store
    const selectedForm = useSelector((state) => state.selectedForm);
    const oidc = useSelector((state) => state.oidc);
    const loadingMessage = useSelector((state) => state.loadingMessage);

    // State
    const [statusPollCount, setStatusPollCount] = useState(0);
    // Erklæringen kan vises som "signert" et lite øyeblikk før det signerte dokumentet er ferdig
    // lagret server-side (rask status-flipp). Håndteres ved å be brukeren prøve nedlasting igjen.
    const [downloadNotReady, setDownloadNotReady] = useState(false);

    // Params
    const submissionId = params.submissionId;

    const isTerminalStatus = (status) => status === "signert" || status === "avvist";

    const handleDownloadButtonClick = () => {
        const accessToken = oidc?.user?.access_token;
        setDownloadNotReady(false);
        dispatch(updateLoadingMessage("Henter signert dokument"));

        dispatch(getSignedDocument(submissionId, accessToken))
            .then((signedDocument) => {
                if (signedDocument?.blob) {
                    saveFileContentFromBlob(signedDocument.blob, signedDocument.filename);
                } else {
                    // Dokumentet er ennå ikke ferdig lagret (klargjøres like etter signering).
                    setDownloadNotReady(true);
                }
            })
            .catch((error) => {
                console.log("handleDownloadButtonClick", error);
                setDownloadNotReady(true);
            })
            .finally(() => {
                dispatch(updateLoadingMessage(null));
            });
    };

    useEffect(() => {
        const fecthFormData = () => {
            dispatch(updateLoadingMessage("Henter innsending"));
            return dispatch(fetchSubmission(submissionId))
                .then((submission) => {
                    if (submission && !submission.fetchError && Object.keys(submission).length) {
                        dispatch(updateLoadingMessage("Henter skjema"));
                        return dispatch(fetchSelectedForm(submission))
                            .then((response) => {
                                dispatch(updateLoadingMessage(null));
                                // fetchSelectedForm resolver til skjemaet (eller null hvis det ikke kunne hentes).
                                return response;
                            })
                            .catch((error) => {
                                console.log("fetchSelectedForm", error);
                                dispatch(updateLoadingMessage(null));
                            });
                    }
                })
                .catch((error) => {
                    console.log("fetchSubmission", error);
                })
                .finally(() => {
                    dispatch(updateLoadingMessage(null));
                });
        };

        const accessToken = oidc?.user?.access_token;
        const hasSelectedForm = selectedForm && Object.keys(selectedForm).length;
        if (!hasSelectedForm && accessToken) {
            fecthFormData();
        }
    }, [selectedForm, oidc, submissionId, dispatch]);

    useEffect(() => {
        const hasSelectedForm = selectedForm && Object.keys(selectedForm).length;
        if (!hasSelectedForm) {
            return;
        }

        const isAuthenticated = oidc?.user && Object.keys(oidc.user).length;
        if (!isAuthenticated) {
            navigate(`/skjema/${selectedForm.referanseId}`);
            return;
        }

        // Terminal status - vis kvitteringen.
        if (isTerminalStatus(selectedForm.status)) {
            return;
        }

        // Signeringen behandles server-side (pollingworkeren har ikke satt "signert" ennå).
        // Oppdater statusen automatisk til den blir terminal, opp til en grense.
        if (statusPollCount >= MAX_STATUS_POLLS) {
            return;
        }

        const pollInterval =
            statusPollCount < FAST_STATUS_POLLS ? FAST_STATUS_POLL_INTERVAL_MS : STATUS_POLL_INTERVAL_MS;

        const timer = setTimeout(() => {
            dispatch(fetchSubmission(submissionId)).then((submission) => {
                if (submission && Object.keys(submission).length) {
                    dispatch(fetchSelectedForm(submission));
                }
            });
            setStatusPollCount((count) => count + 1);
        }, pollInterval);

        return () => clearTimeout(timer);
    }, [selectedForm, oidc, navigate, dispatch, submissionId, statusPollCount]);

    const renderProcessingContent = () => {
        const reachedPollLimit = statusPollCount >= MAX_STATUS_POLLS;
        return (
            <React.Fragment>
                <Header size={2} content="Signeringen er mottatt" />
                <div className={commonStyle.introText}>
                    <div className={commonStyle.paragraphGroup}>
                        <p>
                            Signeringen behandles. Du får kvitteringen på e-post når erklæringen er
                            ferdig behandlet. Du kan trygt lukke denne siden.
                        </p>
                        {reachedPollLimit ? (
                            <p>
                                Det tar litt lengre tid enn vanlig. Du får fortsatt kvitteringen på
                                e-post, og du kan oppdatere siden for å se status.
                            </p>
                        ) : (
                            <LoadingAnimation message="Behandler signeringen" />
                        )}
                    </div>
                    <Header size={3} content="Har du spørsmål?" />
                    <div>{formatContactInfoText(selectedForm)}</div>
                </div>
            </React.Fragment>
        );
    };

    const renderContent = () => {
        switch (selectedForm.status) {
            case "signert":
                const foretakEpost =
                    selectedForm?.formData?.ansvarligForetak?.kontaktpersonEpost ||
                    selectedForm?.formData?.ansvarligForetak?.epost;
                return (
                    <React.Fragment>
                        <Header size={2} content="Erklæringen har blitt signert" />
                        <div className={commonStyle.introText}>
                            <div className={commonStyle.paragraphGroup}>
                                <Header size={3} content="Hvor har erklæringen blitt sendt?" />
                                <p>
                                    Erklæringen er sendt til {selectedForm?.formData?.ansvarligSoeker?.navn}, som er
                                    ansvarlig søker.
                                </p>
                                {foretakEpost ? (
                                    <p>En kopi av den signerte erklæringen er også sendt til {foretakEpost}.</p>
                                ) : (
                                    ""
                                )}
                            </div>
                            <div className={commonStyle.paragraphGroup}>
                                <Header size={3} content="Husk å arkivere erklæringen" />
                                <p>Ansvarlig foretak må selv holde arkiv over signerte erklæringer.</p>
                                <p>Du kan laste ned erklæringen ved å trykke på knappen under.</p>
                            </div>
                            <div className={commonStyle.paragraphGroup}>
                                <Button
                                    onClick={handleDownloadButtonClick}
                                    content="Last ned en kopi"
                                    color="primary"
                                />
                                {downloadNotReady ? (
                                    <p>
                                        Det signerte dokumentet klargjøres fortsatt. Vent et lite øyeblikk og
                                        prøv igjen. Du får uansett kvitteringen på e-post.
                                    </p>
                                ) : null}
                            </div>
                            <Header size={3} content="Har du spørsmål?" />
                            <div>{formatContactInfoText(selectedForm)}</div>
                        </div>
                    </React.Fragment>
                );
            case "avvist":
                return (
                    <React.Fragment>
                        <Header size={2} content="Erklæringen har blitt avvist" />

                        <div className={commonStyle.introText}>
                            {selectedForm?.statusReason?.length ? (
                                <React.Fragment>
                                    <div className={commonStyle.paragraphGroup}>
                                        <Header size={3} content="Du har meldt om følgende feil:" />
                                        <p>
                                            <i>{selectedForm.statusReason}</i>
                                        </p>
                                    </div>
                                    <div className={commonStyle.paragraphGroup}>
                                        <p>
                                            Meldingen er sendt til {selectedForm?.formData?.ansvarligSoeker?.navn}, som
                                            er ansvarlig søker
                                        </p>
                                    </div>
                                </React.Fragment>
                            ) : null}
                            <div className={commonStyle.paragraphGroup}>
                                <p>En kvittering er sendt til ansvarlig foretak på e-post.</p>
                            </div>
                            <Header size={3} content="Har du spørsmål?" />
                            <div>{formatContactInfoText(selectedForm)}</div>
                        </div>
                    </React.Fragment>
                );
            default:
                return renderProcessingContent();
        }
    };

    return (
        <Container>
            <Helmet>
                <meta name="robots" content="noindex"></meta>
            </Helmet>
            <Paper>
                {selectedForm.status === "signert" ? (
                    <MainHeading form={selectedForm} pageName="Signaturen er sendt" />
                ) : selectedForm.status === "avvist" ? (
                    <MainHeading form={selectedForm} pageName="Avvisningen er sendt" />
                ) : null}
                {loadingMessage?.length ? <LoadingAnimation fixed message={loadingMessage} /> : ""}
                {renderContent()}
            </Paper>
        </Container>
    );
};

export default Receipt;
